export default {
  getCroppedFile,
};

// cap the longer edge of the exported image, in pixels, so uploads stay a reasonable size
const MAX_DIMENSION = 1200;
const JPEG_QUALITY = 0.85;

/**
 * Draws the cropped region of a loaded <img> element onto a canvas, downscaling it if
 * needed to stay within MAX_DIMENSION, and exports it as a compressed File. Accounts for
 * the ratio between the image's displayed size and its natural (full-resolution) size.
 * Exports as PNG (lossless) when the crop contains transparency, otherwise as a
 * compressed JPEG, and appends the matching extension to fileName.
 * @param {HTMLImageElement} image - the source image element (already loaded in the DOM)
 * @param {Object} crop - pixel crop relative to the displayed image size ({ x, y, width, height })
 * @param {string} fileName - base name (no extension) to give the resulting File
 * @returns {Promise<File>} the cropped image as a PNG or JPEG File
 */
function getCroppedFile(image, crop, fileName) {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const sourceX = crop.x * scaleX;
  const sourceY = crop.y * scaleY;
  const sourceWidth = crop.width * scaleX;
  const sourceHeight = crop.height * scaleY;

  const downscale = Math.min(
    1,
    MAX_DIMENSION / Math.max(sourceWidth, sourceHeight)
  );
  const outputWidth = Math.round(sourceWidth * downscale);
  const outputHeight = Math.round(sourceHeight * downscale);

  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    outputWidth,
    outputHeight
  );

  const outputType = hasTransparency(ctx, outputWidth, outputHeight)
    ? 'image/png'
    : 'image/jpeg';
  const extension = outputType === 'image/png' ? 'png' : 'jpg';
  const outputFileName = `${fileName}.${extension}`;

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to generate cropped image'));
          return;
        }
        resolve(new File([blob], outputFileName, { type: outputType }));
      },
      outputType,
      outputType === 'image/jpeg' ? JPEG_QUALITY : undefined
    );
  });
}

// Scans the drawn pixels for any non-opaque alpha value, so PNG's larger output is only
// used when the source actually needs transparency; opaque images still get compressed as JPEG.
function hasTransparency(ctx, width, height) {
  const { data } = ctx.getImageData(0, 0, width, height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true;
    }
  }
  return false;
}
